import { memo, useMemo, useState } from "react";
import { Dropdown, Flex, Select } from "@repo/ping-design";
import {
  MIN_ZOOM_REQUIRED_FOR_NEARMAP,
  isInUS,
  SpecialComponentProps,
  LAYER_GOOGLE,
} from "@repo/ping-react-js";
import { DropdownSelectionType } from "@repo/ping-design";
import "./NearmapImagerySelector.scss";

const getDisplayDate = (dateString: string): string => {
  const [year, month, day] = dateString.split("-");
  return `${month}/${day}/${year}`;
};

interface NearmapImagerySelectorProps extends SpecialComponentProps {
  zoom?: number;
  center?: { lat: number; lng: number };
  onDateSelect?: (date: string) => void;
  availableLayers?: Record<string, any>;
  enabledLayer?: { name: string } | null;
  nearmapDates?: string[];
  isLoadingNearmaps?: boolean;
}

export const NearmapImagerySelector = memo(
  ({
    onLayerSelect,
    zoom,
    center,
    onDateSelect,
    availableLayers,
    enabledLayer,
    nearmapDates = [],
    isLoadingNearmaps = false,
  }: NearmapImagerySelectorProps) => {
    const usBasedLocation = center && isInUS(center);
    const canViewNearmaps = !!availableLayers?.NEARMAP;

    const dropdownOptions = useMemo(
      () =>
        nearmapDates.map((date) => ({
          label: getDisplayDate(date),
          value: date,
        })),
      [nearmapDates],
    );

    const [selectedDate, setSelectedDate] = useState<string | null>(
      nearmapDates[0] || null,
    );

    const handleDateChange = (selection: any) => {
      if (selection.type === DropdownSelectionType.SimpleValue) {
        setSelectedDate(selection.value);
        onDateSelect?.(selection.value);
      }
    };

    const showDateDropdown =
      nearmapDates.length > 0 &&
      zoom !== undefined &&
      zoom >= MIN_ZOOM_REQUIRED_FOR_NEARMAP;

    const viewOptions = [
      { label: "Vertical", value: "vertical" },
      { label: "North", value: "north" },
      { label: "South", value: "south" },
      { label: "East", value: "east" },
      { label: "West", value: "west" },
    ];

    const handleViewChange = (_: any, value: string) => {
      if (value === null) {
        onLayerSelect(LAYER_GOOGLE, {});
        return;
      }
      onLayerSelect(
        {
          label: value.charAt(0).toUpperCase() + value.slice(1),
          name: value,
          url: [],
          layerType: "google",
          id: "NEARMAP",
        },
        {
          direction: value,
        },
      );
    };

    const renderViewAndDateSelectors = () => (
      <Flex
        direction="column"
        gap="3"
        className="NearmapImagerySelector__Dropdowns"
      >
        <Select
          options={viewOptions}
          value={enabledLayer?.name || ""}
          selectionType="single"
          onChange={handleViewChange}
          label="View"
          field="view"
          disabled={isLoadingNearmaps || dropdownOptions.length === 0}
          customTemplateColumns="1fr 1fr"
        />
        <Dropdown
          options={dropdownOptions}
          selected={selectedDate}
          onChange={handleDateChange}
          placeholder="mm dd yyyy"
          label="Select Date"
          iconName="Calendar"
          disabled={isLoadingNearmaps || dropdownOptions.length === 0}
        />
      </Flex>
    );

    return (
      <div className="NearmapImagerySelector">
        <h3 className="NearmapImagerySelector__Title">Nearmap Imagery</h3>

        {!canViewNearmaps && (
          <p className="NearmapImagerySelector__Error">
            Nearmap imagery is not available for your account.
          </p>
        )}

        {canViewNearmaps && !usBasedLocation && (
          <p className="NearmapImagerySelector__Error">
            Nearmap imagery is only available for US locations.
          </p>
        )}

        {canViewNearmaps &&
          usBasedLocation &&
          zoom !== undefined &&
          zoom < MIN_ZOOM_REQUIRED_FOR_NEARMAP && (
            <>
              {renderViewAndDateSelectors()}
              <p className="NearmapImagerySelector__Error">
                Zoom in to enable Nearmap imagery.
              </p>
            </>
          )}

        {showDateDropdown && renderViewAndDateSelectors()}

        {!showDateDropdown &&
          canViewNearmaps &&
          usBasedLocation &&
          zoom !== undefined &&
          zoom >= MIN_ZOOM_REQUIRED_FOR_NEARMAP && (
            <>
              {renderViewAndDateSelectors()}
              <p className="NearmapImagerySelector__Error">
                No Nearmap dates available for this location.
              </p>
            </>
          )}
      </div>
    );
  },
);

NearmapImagerySelector.displayName = "NearmapImagerySelector";
